import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/Button";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SEO } from "@/components/seo/SEO";
import { useCommissionSettings } from "@/features/commission-settings/hooks";
import { useSubmitContact } from "@/features/contact/hooks";
import type { CommissionType, ContactSubmissionInput } from "@/features/contact/api";
import { PublicError, PublicLoading } from "@/pages/public/PublicState";

type ContactMode = "GENERAL" | "COMMISSION";

const commissionTypes: CommissionType[] = ["Vehicle Livery", "Uniform Design", "Custom Design", "Other"];

const emptyForm = {
  name: "",
  email: "",
  discord: "",
  subject: "",
  message: "",
  commissionType: "Vehicle Livery" as CommissionType,
  projectName: "",
  communityName: "",
  budget: "",
  deadline: "",
  referenceLinks: "",
  permission: false,
};

export function ContactPage() {
  const submitContact = useSubmitContact();
  const commissionSettings = useCommissionSettings();
  const commissionsOpen = commissionSettings.data?.isOpen ?? false;
  const [mode, setMode] = useState<ContactMode>("GENERAL");
  const [form, setForm] = useState(emptyForm);
  const [notice, setNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  if (commissionSettings.isLoading) return <PublicLoading label="Loading contact options..." />;
  if (commissionSettings.isError) return <PublicError label="Unable to load contact options." />;

  function update<K extends keyof typeof form>(key: K, value: (typeof form)[K]) {
    setForm((current) => ({ ...current, [key]: value }));
  }

  function validate() {
    if (!form.name.trim()) return "Name is required.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "A valid email is required.";
    if (mode === "GENERAL") {
      if (!form.subject.trim()) return "Subject is required.";
      if (form.message.trim().length < 10) return "Message must be at least 10 characters.";
      return null;
    }
    if (!commissionsOpen) return "Commission requests are currently closed.";
    if (!form.discord.trim()) return "Discord is required for commission requests.";
    if (!form.commissionType) return "Commission type is required.";
    if (form.message.trim().length < 20) return "Details must be at least 20 characters.";
    if (!form.permission) return "Please confirm this is a request and not guaranteed acceptance.";
    return null;
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setNotice(null);
    const error = validate();
    if (error) return setNotice({ tone: "error", text: error });

    const input: ContactSubmissionInput =
      mode === "GENERAL"
        ? {
            type: "GENERAL",
            name: form.name,
            email: form.email,
            discord: form.discord || null,
            subject: form.subject,
            message: form.message,
          }
        : {
            type: "COMMISSION",
            name: form.name,
            email: form.email,
            discord: form.discord,
            subject: form.projectName || `${form.commissionType} request`,
            message: form.message,
            commissionType: form.commissionType,
            projectName: form.projectName || null,
            communityName: form.communityName || null,
            budget: form.budget || null,
            deadline: form.deadline || null,
            referenceLinks: form.referenceLinks || null,
          };

    submitContact.mutate(input, {
      onSuccess: () => {
        setForm(emptyForm);
        setNotice({ tone: "success", text: mode === "GENERAL" ? "Message sent." : "Commission request submitted." });
      },
      onError: (err) => setNotice({ tone: "error", text: err instanceof Error ? err.message : "Unable to submit." }),
    });
  }

  return (
    <section className="mx-auto w-full max-w-5xl px-3 py-8 sm:px-6 sm:py-16 lg:px-8">
      <SEO title="Contact" description="Contact Kalaco or submit a commission request." />
      <CyberPanel>
        <p className="text-sm font-semibold uppercase tracking-[0.28em] text-teal-200">Contact</p>
        <h1 className="mt-3 font-display text-3xl font-bold uppercase text-white sm:text-4xl">Send a Message</h1>
        <p className="mt-4 max-w-2xl text-content-secondary">
          Choose a general message or submit a structured commission request.
        </p>

        {!commissionsOpen ? (
          <div className="mt-6 rounded-md border border-red-300/30 bg-red-500/10 p-4 text-sm text-red-100">
            Commissions are currently closed. General contact remains open, but commission request submissions are disabled.
          </div>
        ) : null}

        <div className="mt-8 grid gap-3 sm:grid-cols-2">
          <ModeButton active={mode === "GENERAL"} onClick={() => setMode("GENERAL")}>General Contact</ModeButton>
          <ModeButton active={mode === "COMMISSION"} disabled={!commissionsOpen} onClick={() => setMode("COMMISSION")}>Commission Request</ModeButton>
        </div>

        <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label="Name" value={form.name} onChange={(value) => update("name", value)} required />
            <TextField label="Email" type="email" value={form.email} onChange={(value) => update("email", value)} required />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <TextField label={`Discord${mode === "COMMISSION" ? "" : " optional"}`} value={form.discord} onChange={(value) => update("discord", value)} required={mode === "COMMISSION"} />
            {mode === "GENERAL" ? (
              <TextField label="Subject" value={form.subject} onChange={(value) => update("subject", value)} required />
            ) : (
              <SelectField label="Commission Type" value={form.commissionType} options={commissionTypes} onChange={(value) => update("commissionType", value as CommissionType)} />
            )}
          </div>

          {mode === "COMMISSION" ? (
            <>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField label="Project Name optional" value={form.projectName} onChange={(value) => update("projectName", value)} />
                <TextField label="Server / Community optional" value={form.communityName} onChange={(value) => update("communityName", value)} />
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField label="Budget optional" value={form.budget} onChange={(value) => update("budget", value)} />
                <TextField label="Deadline optional" type="date" value={form.deadline} onChange={(value) => update("deadline", value)} />
              </div>
              <TextField label="Reference Links optional" value={form.referenceLinks} onChange={(value) => update("referenceLinks", value)} />
            </>
          ) : null}

          <label className="block">
            <span className="text-sm text-content-secondary">{mode === "GENERAL" ? "Message" : "Description / Details"}</span>
            <textarea
              className="mt-2 min-h-40 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
              value={form.message}
              onChange={(event) => update("message", event.target.value)}
              required
            />
          </label>

          {mode === "COMMISSION" ? (
            <label className="flex items-start gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
              <input className="mt-1 size-4 accent-cyan-300" type="checkbox" checked={form.permission} onChange={(event) => update("permission", event.target.checked)} />
              <span className="text-sm leading-6 text-content-secondary">
                I understand this is a request and does not guarantee acceptance.
              </span>
            </label>
          ) : null}

          {notice && (
            <p className={notice.tone === "success" ? "rounded-md border border-teal-300/30 bg-teal-300/10 px-3 py-2 text-sm text-teal-100" : "rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100"}>
              {notice.text}
            </p>
          )}

          <Button type="submit" className="w-full sm:w-auto" disabled={submitContact.isPending || (mode === "COMMISSION" && !commissionsOpen)}>
            {submitContact.isPending ? "Submitting..." : mode === "GENERAL" ? "Send Message" : "Submit Request"}
          </Button>
        </form>
      </CyberPanel>
    </section>
  );
}

function ModeButton({ active, disabled, onClick, children }: { active: boolean; disabled?: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={active ? "min-w-0 break-words rounded-md border border-cyan-300/70 bg-cyan-300/10 px-3 py-3 text-xs font-bold uppercase tracking-[0.12em] text-white shadow-neon sm:px-4 sm:text-sm sm:tracking-[0.18em]" : "min-w-0 break-words rounded-md border border-cyan-300/20 bg-black/30 px-3 py-3 text-xs font-bold uppercase tracking-[0.12em] text-content-secondary transition hover:border-cyan-300/60 hover:text-white disabled:cursor-not-allowed disabled:opacity-45 sm:px-4 sm:text-sm sm:tracking-[0.18em]"}
    >
      {children}
    </button>
  );
}

function TextField({ label, value, onChange, type = "text", required }: { label: string; value: string; onChange: (value: string) => void; type?: "text" | "email" | "date"; required?: boolean }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input className="mt-2 h-11 w-full min-w-0 rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300" value={value} onChange={(event) => onChange(event.target.value)} type={type} required={required} />
    </label>
  );
}

function SelectField({ label, value, options, onChange }: { label: string; value: string; options: string[]; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <select className="mt-2 h-11 w-full min-w-0 rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => <option key={option} value={option}>{option}</option>)}
      </select>
    </label>
  );
}
